/**
 * @file 親向けカード用の きき分けあそび現状 (state + 直近 30 日の統計) の取得。
 * @why: 既存の useProgressLogs は先頭 1 頁 (30 件) しか読まず、きき分けの判定・統計には
 *   足りない (敵対的レビュー M3)。ここでは limit=100 で「チェックポイント 2 件 + 30 日ぶん」
 *   まで頁を遡り、系譜が壊れているときだけ全履歴を読む (epic_24 §6.5)。
 * @failure: 取得失敗は status "error" (詳細は console)。ログ 0 件は初期状態 (ドミソのみ・
 *   きょうまだ) として ready を返す。
 */

import { useEffect, useState } from "react";
import { addDaysLocal, todayLocal } from "../api/dates";
import { fetchProgressLogs } from "../api/plans";
import { kikiwakeChord, type ChordId } from "../model/kikiwake/chords";
import { KIKIWAKE_CURRENT_CONFIG } from "../model/kikiwake/kikiwakeConfig";
import {
  diffDays,
  KIKIWAKE_MAX_FETCH_PAGES,
  resolveKikiwakeState,
  type KikiwakeLogInput,
} from "../model/kikiwake/kikiwakeEngine";
import {
  parseKikiwakeDetail,
  type KikiwakeState,
} from "../model/kikiwake/kikiwakeLog";
import { KIKIWAKE_CHORDS } from "../model/kikiwake/chords";

export interface KikiwakeChordStat {
  id: ChordId;
  asked: number;
  ok: number;
}

export interface KikiwakeOverview {
  state: KikiwakeState;
  /** チェックポイントの系譜不一致を検出した (別端末での並行実施など)。 */
  forked: boolean;
  /** きょうのセッション数 (途中離脱の保存も 1 回と数える)。 */
  todayCount: number;
  /** きょう (またはきのう) までの連続実施日数。 */
  streakDays: number;
  /** 直近 30 日の和音別 出題数 / 正答数 (ノーカウントは除く)。 */
  perChord: KikiwakeChordStat[];
  /** 直近 3 日に無音疑いのセッションがある。 */
  mutedSuspectRecent: boolean;
  /** 次の和音までの見込み日数 (毎日つづけたとき)。9 和音そろったら null。 */
  nextEstimateDays: number | null;
}

export type KikiwakeOverviewState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; overview: KikiwakeOverview };

const CANON = KIKIWAKE_CHORDS.map((c) => c.id);

function buildOverview(logs: KikiwakeLogInput[], today: string): KikiwakeOverview {
  const resolved = resolveKikiwakeState(logs, today);
  const cfg = KIKIWAKE_CURRENT_CONFIG;
  const monthAgo = addDaysLocal(today, -29);
  const threeDaysAgo = addDaysLocal(today, -2);

  const asked = new Map<ChordId, number>();
  const ok = new Map<ChordId, number>();
  const dates = new Set<string>();
  let todayCount = 0;
  let mutedSuspectRecent = false;
  for (const log of logs) {
    const parsed = parseKikiwakeDetail(log.detail, CANON);
    if (!parsed) continue;
    dates.add(log.date);
    if (log.date === today) todayCount += 1;
    if (log.date >= threeDaysAgo && parsed.detail.muted_suspect) mutedSuspectRecent = true;
    if (log.date < monthAgo) continue;
    for (const t of parsed.detail.trials) {
      if (t.n) continue;
      asked.set(t.c, (asked.get(t.c) ?? 0) + 1);
      if (t.x === undefined) ok.set(t.c, (ok.get(t.c) ?? 0) + 1);
    }
  }

  let streakDays = 0;
  // きょうやっていなくても連続を切らずに表示する (きのうまでの連続)
  let cursor = dates.has(today) ? today : addDaysLocal(today, -1);
  while (dates.has(cursor)) {
    streakDays += 1;
    cursor = addDaysLocal(cursor, -1);
  }

  let nextEstimateDays: number | null = null;
  const active = resolved.state.active;
  if (active.length < CANON.length) {
    const newest = active[active.length - 1];
    const addedAt = newest !== undefined ? resolved.state.addedAt[newest] : undefined;
    const waitLeft = addedAt !== undefined ? cfg.addWaitDays - diffDays(addedAt, today) : cfg.addWaitDays;
    const missLeft =
      resolved.state.lastMissDate !== null
        ? cfg.missFreeDays - diffDays(resolved.state.lastMissDate, today)
        : 0;
    nextEstimateDays = Math.max(waitLeft, missLeft, 0);
  }

  return {
    state: resolved.state,
    forked: resolved.forked,
    todayCount,
    streakDays,
    perChord: active.map((id) => ({ id, asked: asked.get(id) ?? 0, ok: ok.get(id) ?? 0 })),
    mutedSuspectRecent,
    nextEstimateDays,
  };
}

export function useKikiwakeOverview(
  familyId: string,
  personId: string | null,
): { state: KikiwakeOverviewState; reload: () => void } {
  const [state, setState] = useState<KikiwakeOverviewState>({ status: "loading" });
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    if (!personId) {
      setState({ status: "loading" });
      return;
    }
    const controller = new AbortController();
    setState({ status: "loading" });
    (async () => {
      const today = todayLocal();
      const monthAgo = addDaysLocal(today, -29);
      const logs: KikiwakeLogInput[] = [];
      let kikiwakeSeen = 0;
      let oldestDate = today;
      let cursor: string | undefined;
      let pages = 0;
      do {
        const page = await fetchProgressLogs(familyId, personId, cursor, controller.signal, 100);
        pages += 1;
        for (const item of page.items) {
          const entry: KikiwakeLogInput = { id: item.id, createdAt: item.created_at, date: item.date };
          if (item.detail !== undefined) {
            entry.detail = item.detail;
            if (item.detail.includes('"kikiwake"')) kikiwakeSeen += 1;
          }
          logs.push(entry);
          if (item.date < oldestDate) oldestDate = item.date;
        }
        cursor = page.next_cursor;
      } while (cursor && pages < KIKIWAKE_MAX_FETCH_PAGES && (kikiwakeSeen < 2 || oldestDate >= monthAgo));
      let overview = buildOverview(logs, today);
      if (overview.forked && cursor) {
        // 系譜が壊れているときだけ残り履歴も読んでリプレイし直す (§6.4)。頁数上限で
        // 打ち切っても、和集合不変条件が active の後退を防ぐ (KIKIWAKE_MAX_FETCH_PAGES の @why)
        while (cursor && pages < KIKIWAKE_MAX_FETCH_PAGES) {
          const page = await fetchProgressLogs(familyId, personId, cursor, controller.signal, 100);
          pages += 1;
          for (const item of page.items) {
            const entry: KikiwakeLogInput = { id: item.id, createdAt: item.created_at, date: item.date };
            if (item.detail !== undefined) entry.detail = item.detail;
            logs.push(entry);
          }
          cursor = page.next_cursor;
        }
        if (cursor) console.warn("[kikiwake] history fetch capped at", pages, "pages");
        overview = buildOverview(logs, today);
      }
      if (controller.signal.aborted) return;
      setState({ status: "ready", overview });
    })().catch((err: unknown) => {
      if (controller.signal.aborted) return;
      console.error("[kikiwake] failed to load overview", err);
      setState({ status: "error" });
    });
    return () => controller.abort();
  }, [familyId, personId, generation]);

  return { state, reload: () => setGeneration((g) => g + 1) };
}

/** 表示用: 和音 id → 「あか（ドミソ）」。 */
export function kikiwakeChordLabel(id: ChordId): string {
  const c = kikiwakeChord(id);
  return `${c.colorName}（${c.chordName}）`;
}
